import { toast } from 'sonner';
import FlowToastBanner from '@/components/ui/FlowToastBanner';

/**
 * Cross-page flow toast utility
 * - Persists a single toast id across routes via sessionStorage
 * - Avoids duplicate spinners and noisy toasts
 */

export type FlowToastStatus = 'loading' | 'success' | 'error';

export interface FlowToastState {
  id: string;
  title?: string;
  description?: string;
  status: FlowToastStatus;
  startedAt: number;
  mode?: 'spinner' | 'banner';
  progress?: number; // 0-100 for banner mode
}

const STORAGE_KEY = 'wealthica-flow-toast';

// Throttle state for progress updates to avoid jerky rapid re-renders
const progressUpdateState: Record<string, { lastTs: number; lastProgress: number }> = {};

function saveState(state: FlowToastState) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {/* noop */}
}

function readState(): FlowToastState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as FlowToastState;
  } catch {
    return null;
  }
}

function clearState() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {/* noop */}
}

export function startFlowToast(
  id: string = 'wealthica-flow',
  title: string = 'Processing connection…',
  description?: string
) {
  const prev = readState();
  // If another flow is active, replace it cleanly
  if (prev && prev.status === 'loading' && prev.id !== id) {
    try { toast.dismiss(prev.id); } catch { /* noop */ }
    clearState();
  }
  toast.loading(title, { id, description, duration: Infinity });
  saveState({ id, title, description, status: 'loading', startedAt: Date.now(), mode: 'spinner' });
}

export function startFlowToastBanner(
  id: string = 'wealthica-flow',
  title: string = 'Processing…',
  description?: string,
  progress: number = 10
) {
  const prev = readState();
  // If another flow is active, replace it cleanly
  if (prev && prev.status === 'loading' && prev.id !== id) {
    try { toast.dismiss(prev.id); } catch { /* noop */ }
    clearState();
  }
  toast.custom(() => (
    <FlowToastBanner title={title} description={description} progress={progress} />
  ), { id, duration: Infinity });
  saveState({ id, title, description, status: 'loading', startedAt: Date.now(), mode: 'banner', progress });
}

export function updateFlowToast(
  id: string = 'wealthica-flow',
  status: FlowToastStatus = 'loading',
  title?: string,
  description?: string
) {
  const prev = readState();
  // Ignore updates from a different id while a flow is active to prevent cross-flow collisions
  if (prev && prev.status === 'loading' && prev.id !== id) {
    return;
  }
  if (status === 'loading') {
    if (prev?.mode === 'banner') {
      // Preserve banner mode when continuing loading
      toast.custom(() => (
        <FlowToastBanner title={title || prev?.title || 'Working…'} description={description ?? prev?.description} progress={prev?.progress ?? 10} />
      ), { id, duration: Infinity });
      saveState({ id, title: title || prev?.title, description: description ?? prev?.description, status: 'loading', startedAt: prev?.startedAt || Date.now(), mode: 'banner', progress: prev?.progress ?? 10 });
      return;
    }
    toast.loading(title || 'Working…', { id, description, duration: Infinity });
    saveState({ id, title: title || prev?.title, description: description ?? prev?.description, status: 'loading', startedAt: prev?.startedAt || Date.now(), mode: prev?.mode, progress: prev?.progress });
  } else if (status === 'success') {
    // Replace any active custom/banner toast with a short success
    try { toast.dismiss(id); } catch { /* noop */ }
    toast.success(title || 'Done', { id, description, duration: 1500 });
    clearState();
  } else {
    try { toast.dismiss(id); } catch { /* noop */ }
    toast.error(title || 'Failed', { id, description, duration: 2500 });
    clearState();
  }
}

export function completeFlowToast(
  id: string = 'wealthica-flow',
  success: boolean = true,
  title?: string,
  description?: string
) {
  if (success) {
    toast.success(title || 'Connected', { id, description });
  } else {
    toast.error(title || 'Connection failed', { id, description });
  }
  clearState();
}

export function dismissFlowToast(id: string = 'wealthica-flow') {
  try { toast.dismiss(id); } catch {/* noop */}
  clearState();
}

export function rehydrateFlowToast(): FlowToastState | null {
  const state = readState();
  if (!state) return null;
  if (state.status === 'loading') {
    if (state.mode === 'banner') {
      toast.custom(() => (
        <FlowToastBanner title={state.title || 'Processing…'} description={state.description} progress={state.progress ?? 10} />
      ), { id: state.id, duration: Infinity });
    } else {
      toast.loading(state.title || 'Processing…', { id: state.id, description: state.description, duration: Infinity });
    }
  } else if (state.status === 'success') {
    // If a success state was somehow persisted, show briefly then clear
    toast.success(state.title || 'Done', { id: state.id, description: state.description, duration: 1500 });
    clearState();
  } else if (state.status === 'error') {
    toast.error(state.title || 'Failed', { id: state.id, description: state.description, duration: 2500 });
    clearState();
  }
  return state;
}

export function isFlowToastActive(): boolean {
  try { return !!sessionStorage.getItem(STORAGE_KEY); } catch { return false; }
}

export function updateFlowProgress(
  id: string = 'wealthica-flow',
  progress: number,
  title?: string,
  description?: string
) {
  const prev = readState();
  // Ignore progress updates from a different id while a flow is active
  if (prev && prev.status === 'loading' && prev.id !== id) {
    return;
  }
  const now = Date.now();
  const last = progressUpdateState[id] || { lastTs: 0, lastProgress: 0 };
  const deltaMs = now - last.lastTs;
  const deltaPct = Math.abs(progress - last.lastProgress);

  // Throttle: only update if >350ms since last paint or big jump (>7%), or when finishing
  if (progress < 100 && deltaMs < 350 && deltaPct <= 7) {
    return;
  }

  progressUpdateState[id] = { lastTs: now, lastProgress: progress };

  const mode: 'spinner' | 'banner' = 'banner';
  toast.custom(() => (
    <FlowToastBanner title={title || prev?.title || 'Working…'} description={description ?? prev?.description} progress={progress} />
  ), { id, duration: Infinity });
  saveState({ id, title: title || prev?.title, description: description ?? prev?.description, status: 'loading', startedAt: prev?.startedAt || Date.now(), mode, progress });
  if (progress >= 100) {
    // Auto-dismiss after a short delay if no success/error follows
    setTimeout(() => {
      const stateNow = readState();
      if (stateNow && stateNow.id === id && stateNow.status === 'loading') {
        try { toast.dismiss(id); } catch { /* noop */ }
        clearState();
      }
    }, 1000);
  }
}
